import type { RolePermissionScreen } from '@/store/modules/rolePermission';

/** Same codes as `routePermissionRules` entry for prefix `/master` (router/index.ts). */
export const MASTER_ROUTE_SCREEN_CODES = [
  'SUB_CATEGORY',
  'ITEM',
  'PACKAGE',
  'PRODUCT',
  'FINANCE',
  'COMPANY_GROUP',
  'COMPANY',
  'SHOP',
  'SUPPORT_SHOP',
  'SUPPLIER',
  'CAR',
  'CAR_MAKER',
  'MCCS_SETTINGS_ITEM',
  'MCCS_FINANCE_SETTINGS',
  'ORDER_AUTO_MAP',
  'ROLE_PERMISSION_SETTINGS'
] as const;

/** Default home path for new roles and when API omits `homePath`. */
export const DEFAULT_ROLE_HOME_PATH = '/master' as const;

/** Edit form / detail display: pending draft, else effective path. */
export function resolveRoleHomePathForEdit(
  homePath: string | null | undefined,
  homePathDraft: string | null | undefined
): string {
  const draft = homePathDraft?.trim();
  if (draft) return draft;
  const path = homePath?.trim();
  if (path) return path;
  return DEFAULT_ROLE_HOME_PATH;
}

export type HomePathSelectOption = {
  path: string;
  label: string;
  /** If true, at least one listed root screen must have 閲覧のみ or higher. */
  requireAny: boolean;
  requiredScreenCodes: readonly string[];
  /** Shown when `requireAny` and no screen meets VIEW+. */
  validationMessageJa: string;
};

export const HOME_PATH_OPTIONS: HomePathSelectOption[] = [
  {
    path: '/accept',
    label: '受注情報',
    requireAny: true,
    requiredScreenCodes: ['ACCEPT'],
    validationMessageJa:
      '受注情報をホームに設定する場合、「受注管理-受注」で閲覧のみ以上の権限を設定してください。'
  },
  {
    path: '/ship',
    label: '出荷管理',
    requireAny: true,
    requiredScreenCodes: ['SHIPPING'],
    validationMessageJa:
      '出荷管理をホームに設定する場合、「出荷管理」で閲覧のみ以上の権限を設定してください。'
  },
  {
    path: '/harness',
    label: 'ハーネス適合表',
    requireAny: true,
    requiredScreenCodes: ['HARNESS'],
    validationMessageJa:
      'ハーネス適合表をホームに設定する場合、で閲覧のみ以上の権限を設定してください。'
  },
  {
    path: '/master',
    label: 'マスタ管理',
    requireAny: true,
    requiredScreenCodes: [...MASTER_ROUTE_SCREEN_CODES],
    /** Fallback when screen catalog is empty; normally a dynamic list is built. */
    validationMessageJa:
      'マスタ管理をホームに設定する場合、次のいずれかの画面で閲覧のみ以上の権限を設定してください。'
  }
];

/** Builds master home-path validation text: names as 「A」、「B」… in one line. */
export function formatMasterHomePathValidationMessageJa(
  screens: RolePermissionScreen[],
  /** Resolved root codes present in `screens`; if empty, all router master codes are listed. */
  resolvedCodes: string[]
): string {
  const codes =
    resolvedCodes.length > 0 ? resolvedCodes : [...MASTER_ROUTE_SCREEN_CODES];
  const labels = codes.map((code) => {
    const screen = screens.find((s) => s.code === code);
    return screen?.name ?? code;
  });
  const quoted = labels.map((label) => `「${label}」`).join('、');
  return `マスタ管理をホームに設定する場合、次のいずれかの画面で閲覧のみ以上の権限を設定してください。${quoted}`;
}

export type HomePathPermissionValue =
  | 'full'
  | 'edit'
  | 'view'
  | 'limited'
  | 'none';

export type HomePathPermissionRow = {
  screenId?: number;
  kind: 'screen' | 'button';
  permission?: HomePathPermissionValue;
  draft?: HomePathPermissionValue;
  children?: HomePathPermissionRow[];
};

export function isViewOrHigher(
  value: HomePathPermissionValue | undefined
): boolean {
  return value === 'view' || value === 'edit' || value === 'full';
}

function findRootScreenRow(
  rows: HomePathPermissionRow[],
  rootScreenId: number
): HomePathPermissionRow | null {
  for (const row of rows) {
    if (row.kind === 'screen' && row.screenId === rootScreenId) return row;
  }
  return null;
}

function resolveCodesForOption(
  option: HomePathSelectOption,
  screens: RolePermissionScreen[]
): string[] {
  const roots = new Set(screens.map((s) => s.code));
  return option.requiredScreenCodes.filter((code) => roots.has(code));
}

/**
 * Returns an error message (JA) if the chosen home path is not allowed for the
 * current permission matrix; otherwise `null`.
 */
export function validateHomePathAgainstPermissions(
  homePath: string,
  screens: RolePermissionScreen[],
  rows: HomePathPermissionRow[],
  valueKey: 'permission' | 'draft'
): string | null {
  const trimmed = homePath?.trim() ?? '';
  if (!trimmed) return null;

  const option = HOME_PATH_OPTIONS.find((o) => o.path === trimmed);
  if (!option) return null;

  const codes = resolveCodesForOption(option, screens);
  if (!codes.length) {
    if (option.path === '/master') {
      return formatMasterHomePathValidationMessageJa(screens, []);
    }
    return option.validationMessageJa;
  }

  const meets = (code: string): boolean => {
    const root = screens.find((s) => s.code === code);
    if (!root) return false;
    const row = findRootScreenRow(rows, root.id);
    const v = row ? row[valueKey] : undefined;
    return isViewOrHigher(v);
  };

  if (option.requireAny) {
    if (!codes.some(meets)) {
      if (option.path === '/master') {
        return formatMasterHomePathValidationMessageJa(screens, codes);
      }
      return option.validationMessageJa;
    }
  } else if (!codes.every(meets)) {
    if (option.path === '/master') {
      return formatMasterHomePathValidationMessageJa(screens, codes);
    }
    return option.validationMessageJa;
  }
  return null;
}

export function homePathSelectLabel(path: string | null | undefined): string {
  if (!path) return '';
  const opt = HOME_PATH_OPTIONS.find((o) => o.path === path);
  return opt?.label ?? path;
}
